import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, useMap } from '../../lib/react-leaflet-lite';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useBasemapTile } from '../../config/basemaps';
import { MARKER_SIZE, PIN_HEIGHT, PIN_WIDTH, markerHtml } from './orderMapMarkers';

// Lee el tema desde el <html> (ThemeProvider setea data-theme/clase) de forma
// reactiva, sin acoplar el componente al contexto del provider.
function useIsDark() {
  const read = () => {
    const root = document.documentElement;
    return root.getAttribute('data-theme') === 'dark' || root.classList.contains('dark');
  };
  const [isDark, setIsDark] = useState(read);
  useEffect(() => {
    const root = document.documentElement;
    const obs = new MutationObserver(() => setIsDark(read()));
    obs.observe(root, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    setIsDark(read());
    return () => obs.disconnect();
  }, []);
  return isDark;
}

function markerIcon(kind, attempt) {
  const pin = kind === 'address';
  return L.divIcon({
    className: `pd-marker pd-marker--${kind}`,
    html: markerHtml(kind, attempt),
    iconSize: pin ? [PIN_WIDTH, PIN_HEIGHT] : [MARKER_SIZE, MARKER_SIZE],
    iconAnchor: pin ? [PIN_WIDTH / 2, PIN_HEIGHT - 1] : [MARKER_SIZE / 2, MARKER_SIZE / 2],
    popupAnchor: pin ? [0, -PIN_HEIGHT + 2] : [0, -MARKER_SIZE / 2],
  });
}

// Contenido del popup con textContent: motivo y fechas vienen del backend y no
// se interpretan como HTML.
function popupContent({ title, lines }) {
  const root = document.createElement('div');
  root.className = 'pd-marker-popup';
  const head = document.createElement('strong');
  head.className = 'pd-marker-popup__title';
  head.textContent = title;
  root.appendChild(head);
  (lines || []).filter(Boolean).forEach((line) => {
    const row = document.createElement('span');
    row.className = 'pd-marker-popup__line';
    row.textContent = line;
    root.appendChild(row);
  });
  return root;
}

const toLatLng = (p) => [p.coords.latitude, p.coords.longitude];

const highlight = (markers, selectedKey) => {
  markers.forEach((m, key) => {
    m.getElement()?.classList.toggle('pd-marker--selected', key === selectedKey);
  });
};

function PointsLayer({ points, selected, onSelect }) {
  const map = useMap();
  const markersRef = useRef(new Map());
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const selectedKeyRef = useRef(selected?.key);
  selectedKeyRef.current = selected?.key;

  // Clave estable: los puntos se recalculan en cada render del detalle, pero el
  // mapa sólo se rehace si cambió lo que se dibuja.
  const pointsKey = points
    .map((p) => `${p.key}:${p.kind}:${p.coords.latitude},${p.coords.longitude}:${p.title}:${(p.lines || []).join('|')}`)
    .join(';');

  useEffect(() => {
    if (!map || points.length === 0) return undefined;
    const layers = [];
    const markers = markersRef.current;
    markers.clear();

    // Línea punteada de la dirección a cada evento: hace visible a ojo cuánto se
    // alejó el repartidor, sin tener que abrir cada punto.
    const address = points.find((p) => p.kind === 'address');
    if (address) {
      points
        .filter((p) => p.kind !== 'address')
        .forEach((p) => {
          const line = L.polyline([toLatLng(address), toLatLng(p)], {
            className: `pd-map-link pd-map-link--${p.kind}`,
            interactive: false,
            weight: 2,
            dashArray: '4 6',
          });
          line.addTo(map);
          layers.push(line);
        });
    }

    points.forEach((p) => {
      const marker = L.marker(toLatLng(p), {
        icon: markerIcon(p.kind, p.attempt),
        title: p.title,
        alt: p.title,
        keyboard: true,
        riseOnHover: true,
        // La dirección queda debajo: un evento encima de ella no debe taparse.
        zIndexOffset: p.kind === 'address' ? 0 : 500,
      });
      marker.bindPopup(popupContent(p), { closeButton: false, minWidth: 180, maxWidth: 280, autoPanPadding: [24, 24] });
      // popupopen y no click: con teclado (Enter) Leaflet abre el popup sin click.
      marker.on('popupopen', () => onSelectRef.current?.(p.key));
      marker.on('popupclose', () => {
        if (selectedKeyRef.current === p.key) onSelectRef.current?.(null);
      });
      marker.addTo(map);
      // El ícono es role="button": sin esto, el intento se anuncia sólo por su número.
      marker.getElement()?.setAttribute('aria-label', p.title);
      markers.set(p.key, marker);
      layers.push(marker);
    });

    const latLngs = points.map(toLatLng);
    if (latLngs.length === 1) {
      map.setView(latLngs[0], 16, { animate: false });
    } else {
      // El margen deja aire para los marcadores (28 px; el pin crece hacia arriba).
      map.fitBounds(L.latLngBounds(latLngs).pad(0.3), { animate: false, maxZoom: 18 });
    }

    // Reconstruido el mapa (refresco del pedido, cambio de idioma) se conserva el
    // resaltado, pero no se vuelve a mover hacia el punto elegido.
    highlight(markers, selectedKeyRef.current);

    return () => {
      // Quitar un marcador cierra su popup: no es una acción del usuario.
      markers.forEach((m) => m.off('popupclose'));
      layers.forEach((layer) => map.removeLayer(layer));
      markers.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, pointsKey]);

  // Selección desde fuera (leyenda, bitácora, franja): centra y abre el punto.
  // `selected.nonce` permite volver a pedir el mismo punto.
  useEffect(() => {
    if (!map) return;
    highlight(markersRef.current, selected?.key);
    const marker = selected?.key ? markersRef.current.get(selected.key) : null;
    // Abierto desde el propio marcador: Leaflet ya mostró su popup; no se mueve el mapa.
    if (!marker || selected.fromMap) return;
    map.setView(marker.getLatLng(), Math.max(map.getZoom(), 17), { animate: !selected.reduceMotion });
    marker.openPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selected?.key, selected?.nonce]);

  return null;
}

/**
 * Mapa del detalle de pedido. `points`: [{ key, kind, coords, attempt?, title,
 * lines? }] con kind ∈ address | failed | delivered | returned. `title` y
 * `lines` ya vienen traducidos; se usan para el popup y el nombre accesible.
 */
export default function LeafletMap({ points = [], selected, onSelect, id, className, ariaLabel }) {
  const { t } = useTranslation();
  const isDark = useIsDark();
  // Tile del basemap activo (fuente única configurable desde Configuración).
  const tile = useBasemapTile(isDark);

  if (points.length === 0) {
    return (
      <div className={`map-container ${className || ''}`} style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>{t("No hay coordenadas disponibles")}</p>
      </div>
    );
  }

  return (
    <div id={id} className={`map-container ${className || ''}`} style={{ height: '400px', width: '100%' }}>
      <MapContainer
        center={toLatLng(points[0])}
        zoom={14}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        role="region"
        aria-label={ariaLabel}
      >
        <TileLayer
          key={tile.url}
          attribution={tile.attribution}
          url={tile.url}
          subdomains={tile.subdomains}
          maxZoom={tile.maxZoom}
        />
        <PointsLayer points={points} selected={selected} onSelect={onSelect} />
      </MapContainer>
    </div>
  );
}
